<template>
  <div
    :class="[
      { 'order-container': !errorNonMonthlyTag },
      { 'order-container--error': errorNonMonthlyTag },
    ]"
  >
    <!-- Header order: checkbox, collapse toggle, nomor order, total unit, dan tag info -->
    <div class="order-header" :class="[{ 'border-0': isCollapsed }]">
      <div class="d-flex align-items-center" style="gap: 10px">
        <input
          v-if="!disabledInput"
          type="checkbox"
          class="order-checkbox"
          :checked="isChecked"
          @change="onCheckboxChange"
          @click.stop
        />

        <button
          type="button"
          class="collapse-btn"
          :class="{ 'is-collapsed': isCollapsed }"
          @click="toggleCollapse"
          aria-label="Toggle order"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 9l6 6 6-6"
              stroke="#2E3646"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <span class="order-title" @click="toggleCollapse"
          >{{ order.orderCode }} (Batch {{ order.batch }})</span
        >

        <span class="order-unit-badge">{{ order.totalUnits }} unit</span>

        <div class="d-flex justify-content-end" style="flex: 1">
          <span
            class="tag-customization"
            :class="[
              {
                'custom-tag': order.leaseTag !== null,
                'default-tag': order.leaseTag === null,
              },
            ]"
            >{{ metaLabel }}</span
          >
          <span
            class="order-meta-tag ml-2"
            :class="[
              {
                'full-month-tag': isFullMonthTag,
                'standard-tag': isStandardTag,
              },
            ]"
            >{{ currentTag }}</span
          >
        </div>
      </div>
      <small class="text-danger m-0" v-if="errorNonMonthlyTag">
        Order ini menggunakan tag
        <strong>{{ order.billingPer ? order.billingPer : "N/A" }}</strong
        >. Silakan atur ulang tag menggunakan <strong>Atur Sendiri</strong>
      </small>
    </div>

    <!-- Body order: berisi daftar BillingGroupCard -->
    <div v-show="!isCollapsed" class="order-body">
      <payment-group
        v-for="(group, idx) in order.groupOrder"
        :key="group.id || idx"
        :group="group"
        class="order-group-item"
        @toggle="onGroupToggle(group, $event)"
        :prevDataStartRent="
          order.groupOrder[idx - 1] ? order.groupOrder[idx - 1].startRent : null
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ComponentGuard from "../../../Permissions/ComponentGuard.vue";

import PaymentGroup from "./PaymentGroup.vue";
import mixin from "../../mixin.js";

export default {
  name: "OrderContainer",

  mixins: [mixin],

  components: {
    PaymentGroup,
    ComponentGuard,
  },

  props: {
    order: {
      type: Object,
      required: true,
    },
    // Dua-arah checkbox: v-model="selected" di parent
    value: {
      type: Boolean,
      default: false,
    },
    defaultCollapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isCollapsed: this.defaultCollapsed,
    };
  },

  computed: {
    ...mapGetters("rentAdjustment", {
      disabledNotDraft: "getDisabledNotDraft",
    }),
    isChecked() {
      return this.value;
    },
    isFullMonthTag() {
      if (!this.order.leaseTag) {
        return this.order.billingCalculation === "Full Month";
      }
      return this.order.leaseTag.billingCalculation === "Full Month";
    },
    isStandardTag() {
      if (!this.order.leaseTag) {
        return this.order.billingCalculation === "Standard";
      }
      return this.order.leaseTag.billingCalculation === "Standard";
    },
    metaLabel() {
      if (this.order.leaseTag) {
        return "Atur Sendiri";
      }
      return "Ikuti Tag Order Lama";
    },
    currentTag() {
      const orderCurrentTag = this.order.leaseTag || this.order;
      return this.displayedTag(orderCurrentTag);
    },
    errorNonMonthlyTag() {
      if (this.order.leaseTag) {
        return !this.order.leaseTag.tag.includes(this.tagMonthly);
      }
      return this.order.billingPer != "Monthly Invoice";
    },
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit("toggle", this.isCollapsed);
    },

    onCheckboxChange(e) {
      // emit event standar v-model Vue 2 ('input')
      this.$emit("input", e.target.checked);
      this.$emit("check-change", e.target.checked);
    },

    onGroupToggle(group, collapsedState) {
      this.$emit("group-toggle", { group, collapsed: collapsedState });
    },
  },
};
</script>

<style scoped>
.order-container {
  border: 1px solid #e2e5ea;
  border-radius: 12px;
  background: #f7f8fa;
  overflow: hidden;
}

.order-container--error {
  border: 1px solid #f87171;
  border-radius: 12px;
  background: #fef2f2;
  overflow: hidden;
}

/* ---------- Header ---------- */
.order-header {
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
}

.order-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2563eb;
  border: 1px solid #555358;
  flex-shrink: 0;
}

.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 0;
  cursor: pointer;
  transition: transform 0.18s ease;
  flex-shrink: 0;
  transform: rotate(-180deg);
}
.collapse-btn.is-collapsed {
  transform: rotate(0deg);
}

.order-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1f2430;
  cursor: pointer;
}

.order-unit-badge {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  background: #eef0f3;
  border-radius: 999px;
  padding: 3px 10px;
}

.order-meta-tag,
.tag-customization {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 4px 10px;
}

.full-month-tag {
  color: #1d4ed8;
  background: #dbeafe;
}

.standard-tag {
  color: #5a6482;
  background: #efeff3;
}

.custom-tag {
  color: #9c5a16;
  background: #fceedf;
}

.default-tag {
  color: #1e7a5c;
  background: #eaf3ef;
}

/* ---------- Body ---------- */
.order-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* .order-group-item { */
/* jarak antar BillingGroupCard sudah diatur lewat gap pada .order-body */
/* } */
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/CustomComponent/PaymentPreview/OrderContainer.vue